import React, { useState } from 'react';
import { PROGRAMME_ROUTES } from '../../data/programmeRoutes';

/* ===========================================================================
   Two routes in
   The single most important thing a candidate needs to understand before
   applying, so it sits directly under the track list.
   =========================================================================== */
export function ProgrammeRoutes() {
  return (
    <section className="sp-section">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <span className="sp-eyebrow">Two routes</span>
            <h2 className="sp-heading">Join directly, or train with us first</h2>
            <p className="sp-lead" style={{ marginBottom: '42px' }}>
              Both routes run through the same two interview rounds and both end with an
              internship certificate. The difference is what happens between selection and
              your first live project.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {PROGRAMME_ROUTES.map((route) => (
            <div className="col-lg-6" key={route.value}>
              <div className="sp-benefit" style={{ height: '100%' }}>
                <h3 className="sp-track__title">{route.label}</h3>
                <p className="sp-track__tagline">{route.summary}</p>
                <p className="sp-benefit__text" style={{ marginBottom: 18 }}>
                  {route.description}
                </p>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {route.points.map((point) => (
                    <li key={point} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      padding: '7px 0', fontSize: '0.92rem', color: '#5b6270',
                    }}>
                      <i className="fa-solid fa-check" aria-hidden="true"
                        style={{ color: '#dc2626', marginTop: 4, flexShrink: 0 }} />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <p className="sp-lead" style={{ marginTop: 30, fontSize: '0.95rem' }}>
          Whether a placement carries a stipend is decided after selection, based on the
          role and your interview outcome. The terms are confirmed in your offer letter
          before you commit to anything.
        </p>
      </div>
    </section>
  );
}

/* ===========================================================================
   Why intern here
   =========================================================================== */
const BENEFITS = [
  {
    icon: 'fa-diagram-project',
    title: 'Live projects, not sample projects',
    text: 'After selection you are deployed to work that has a client and a deadline behind it. What you build gets used.',
  },
  {
    icon: 'fa-chalkboard-user',
    title: 'Training first, if you need it',
    text: 'On the Training + Internship route, our expert team teaches your chosen course before you go anywhere near a live system.',
  },
  {
    icon: 'fa-user-tie',
    title: 'One named mentor',
    text: 'A senior team member owns your progress, reviews your work and writes your final assessment.',
  },
  {
    icon: 'fa-certificate',
    title: 'Certificate on completion',
    text: 'Complete the internship and submit your project, and we issue an internship certificate. Strong performers also get a written recommendation.',
  },
  {
    icon: 'fa-code-branch',
    title: 'Practical over theoretical',
    text: 'The point of the project phase is applying what you were taught. You leave having built something, not having watched someone build it.',
  },
  {
    icon: 'fa-briefcase',
    title: 'A route to a full-time role',
    text: 'Interns who perform well are considered for open positions on their team. The internship is effectively the interview.',
  },
];

export function WhyIntern() {
  return (
    <section className="sp-section sp-section--tint">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <span className="sp-eyebrow">Why Starparth</span>
            <h2 className="sp-heading">An internship that reads well on a CV because it was real</h2>
            <p className="sp-lead" style={{ marginBottom: '46px' }}>
              We run examination infrastructure, IT consulting, marketing and financial advisory for
              clients across India. Interns are placed inside those teams, not beside them.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {BENEFITS.map((b) => (
            <div className="col-lg-4 col-md-6" key={b.title}>
              <div className="sp-benefit">
                <div className="sp-benefit__icon" aria-hidden="true">
                  <i className={`fa-solid ${b.icon}`} />
                </div>
                <h3 className="sp-benefit__title">{b.title}</h3>
                <p className="sp-benefit__text">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===========================================================================
   Selection process
   The numbering here is meaningful: these steps happen in this order, and
   the visitor needs to know where they are in the sequence.
   =========================================================================== */
const STEPS = [
  { title: 'Apply', text: 'Fill the form below, choose your route and attach your resume.' },
  { title: 'Screening', text: 'We review applications and reply either way within seven working days.' },
  { title: 'Interview 1', text: 'A conversation about your interests, your background and one thing you have built.' },
  { title: 'Interview 2', text: 'A deeper technical or role-specific round with the team you would join.' },
  { title: 'Selection', text: 'You get an offer letter with your start date, route and terms confirmed.' },
  { title: 'Training', text: 'On the Training + Internship route, expert-led teaching on your chosen course.' },
  { title: 'Live project', text: 'Deployment to real project work, applying what you have learned, with mentor review.' },
  { title: 'Certificate', text: 'Submit your project and complete the term to receive your internship certificate.' },
];

export function InternshipProcess() {
  return (
    <section className="sp-section">
      <div className="container">
        <div className="row justify-content-center text-center">
          <div className="col-lg-8">
            <span className="sp-eyebrow" style={{ justifyContent: 'center' }}>How it works</span>
            <h2 className="sp-heading">From application to certificate</h2>
            <p className="sp-lead" style={{ margin: '0 auto 54px' }}>
              Selection takes about two weeks and runs through two interview rounds. We tell
              you where you stand at every stage. If it is a no, you hear that too.
            </p>
          </div>
        </div>

        <ol className="sp-steps" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {STEPS.map((step, i) => (
            <li className="sp-step" key={step.title}>
              <div className="sp-step__num">{String(i + 1).padStart(2, '0')}</div>
              <h3 className="sp-step__title">{step.title}</h3>
              <p className="sp-step__text">{step.text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ===========================================================================
   Gallery
   =========================================================================== */
const PHOTOS = [
  { src: '/assets/images/Exam_Snapshots/Picture1.png', caption: 'Exam-day control room, Jaipur' },
  { src: '/assets/images/IT_Consulting_Images/collabrative-culture.png', caption: 'Weekly mentor review' },
  { src: '/assets/images/Exam_Snapshots/Picture7.png', caption: 'Centre readiness audit' },
  { src: '/assets/images/about/about-4.jpg', caption: 'Campaign planning session' },
  { src: '/assets/images/MANPOWER_MANAGEMENT/Picture2.png', caption: 'Field team briefing' },
];

export function LifeAtStarparth() {
  return (
    <section className="sp-section sp-section--tint">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-5">
            <span className="sp-eyebrow">On the ground</span>
            <h2 className="sp-heading">Where you will actually be</h2>
            <p className="sp-lead">
              Our office is in Chitrakoot, Jaipur, and much of the work happens at examination
              centres across Rajasthan. Onsite tracks include field days — you will see a centre
              set up, run and shut down.
            </p>
            <p className="sp-lead">
              Hybrid tracks work from the office three days a week. Whichever track you join, you
              get a desk, a laptop if you need one, and lunch on office days.
            </p>
          </div>

          <div className="col-lg-7">
            <div className="sp-gallery">
              {PHOTOS.map((photo) => (
                <figure key={photo.src}>
                  <img
                    src={photo.src}
                    alt={photo.caption}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = '/assets/images/about/about-1.jpg'; }}
                  />
                  <figcaption>{photo.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===========================================================================
   FAQ
   =========================================================================== */
const FAQS = [
  {
    q: 'What is the difference between the two routes?',
    a: 'The Internship route puts you straight onto live project work after selection, and suits candidates who already have the fundamentals for their track. The Training + Internship route starts with expert-led training on your chosen course, and you are deployed to a live project afterwards to apply it. Both run through the same two interview rounds and both end with a certificate.',
  },
  {
    q: 'Is the internship paid?',
    a: 'Not always. Whether a placement carries a stipend is decided by the organisation after you are selected, based on the role and your interview outcome. We tell you the terms in your offer letter before you commit to anything — you will never be asked to start without knowing.',
  },
  {
    q: 'Who can apply?',
    a: 'Students in their second year onward, final-year students, and recent graduates from any recognised college. Several tracks suit non-engineering backgrounds well — HR, marketing and operations in particular.',
  },
  {
    q: 'What happens in the two interview rounds?',
    a: 'The first is a conversation about your background, your interests and something you have built or organised. The second is a deeper round with the team you would actually join, focused on the work itself. Between them we are checking fit for the track, not testing you on trivia.',
  },
  {
    q: 'Do I need prior experience?',
    a: 'No. That is what the Training + Internship route exists for. We look for one thing you have built or organised and can talk about clearly — a college project, a club you ran, a freelance job. Skill gaps are what the programme addresses.',
  },
  {
    q: 'How do I get the certificate?',
    a: 'Complete your term and submit your project. The certificate is issued on successful completion of both — it reflects work you actually finished, which is what makes it worth showing to an employer.',
  },
  {
    q: 'Can I do this alongside college?',
    a: 'Hybrid and remote tracks can often be arranged around class timetables — tell us your constraints in the application. Onsite tracks need you present on exam days, which are usually weekends.',
  },
  {
    q: 'When do intakes start, and how long until I hear back?',
    a: 'We run intakes in January, May and September, and fill from a rolling shortlist. Applying between intakes is fine, as we hold strong applications for the next cycle. We reply either way within seven working days of screening.',
  },
];

export function InternshipFaq() {
  const [open, setOpen] = useState(0);

  return (
    <section className="sp-section">
      <div className="container">
        <div className="row g-5">
          <div className="col-lg-4">
            <span className="sp-eyebrow">Questions</span>
            <h2 className="sp-heading">Before you apply</h2>
            <p className="sp-lead">
              Something not covered here? Email{' '}
              <a href="mailto:info@starparth.com" style={{ color: '#dc2626' }}>info@starparth.com</a>{' '}
              or call +91 90570 00445.
            </p>
          </div>

          <div className="col-lg-8">
            <div className="sp-faq">
              {FAQS.map((faq, i) => (
                <div className={`sp-faq__item${open === i ? ' is-open' : ''}`} key={faq.q}>
                  <button
                    type="button"
                    className="sp-faq__q"
                    aria-expanded={open === i}
                    aria-controls={`faq-panel-${i}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                  >
                    {faq.q}
                    <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                  </button>
                  <div className="sp-faq__a" id={`faq-panel-${i}`} role="region">
                    <div><p>{faq.a}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
